import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Grid, Box, Modal } from "@mui/material";
import { toast } from "react-toastify";

import {
  listDischargeTemplates,
  rememberTemplateOriginalName,
  getRememberedTemplateOriginalName,
  deleteDischargeTemplate,
} from "../api/dischargeTemplateApi";

function getFileNameFromS3Path(s3Path) {
  if (!s3Path) return null;
  const withoutQuery = s3Path.split("?")[0];
  const parts = withoutQuery.split("/");
  try {
    return decodeURIComponent(parts[parts.length - 1]) || null;
  } catch (err) {
    return parts[parts.length - 1] || null;
  }
}

function isGenericStoredName(fileName, templateId) {
  if (!fileName) return true;
  const base = fileName.replace(/\.[^.]+$/, "");
  return String(base) === String(templateId);
}

function getDisplayName(template) {
  const remembered = getRememberedTemplateOriginalName(template.id);
  const s3Name = getFileNameFromS3Path(template.s3_path);
  return (
    template.original_name ||
    template.file_name ||
    template.name ||
    remembered ||
    (!isGenericStoredName(s3Name, template.id) ? s3Name : null) ||
    s3Name ||
    `Template ${template.id}`
  );
}

function getExtension(fileName) {
  const match = String(fileName || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : "";
}

function getPreviewUrl(s3Path, ext) {
  if (!s3Path) return null;
  // PDFs use browser's native viewer
  if (ext === "pdf") return s3Path;
  // .doc/.docx use Google Docs Viewer (lighter than Office Viewer)
  if (ext === "doc" || ext === "docx") {
    return `https://docs.google.com/gview?url=${encodeURIComponent(s3Path)}&embedded=true`;
  }
  return null;
}

function DischargeTemplateList({ refreshList, lastUploadedFileName }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    listDischargeTemplates({})
      .then((res) => {
        if (res?.status === "Success") {
          const templateList = Array.isArray(res?.data?.templates)
            ? res.data.templates
            : Array.isArray(res?.data)
            ? res.data
            : [];

          if (lastUploadedFileName && templateList.length > 0) {
            const newest = templateList.reduce((a, b) =>
              Number(a.id) > Number(b.id) ? a : b
            );
            rememberTemplateOriginalName(newest.id, lastUploadedFileName);
          }

          setTemplates(templateList);
        } else {
          toast.error(res?.message || "Could not load templates");
          setTemplates([]);
        }
      })
      .catch((err) => {
        console.error("listDischargeTemplates:", err);
        toast.error(err?.response?.data?.message || "Could not load templates");
        setTemplates([]);
      })
      .finally(() => setLoading(false));
  }, [lastUploadedFileName]);

  useEffect(() => {
    fetchTemplates();
  }, [refreshList]);

  const handleDelete = useCallback((templateId, templateName) => {
    if (!window.confirm(`Delete "${templateName}"? This cannot be undone.`)) {
      return;
    }
    deleteDischargeTemplate(templateId)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Template deleted successfully");
          fetchTemplates();
        } else {
          toast.error(res?.message || "Could not delete template");
        }
      })
      .catch((err) => {
        console.error("deleteDischargeTemplate:", err);
        toast.error(err?.response?.data?.message || "Could not delete template");
      });
  }, []);

  const previewName = previewTemplate ? getDisplayName(previewTemplate) : "";
  const previewExt = previewTemplate
    ? getExtension(previewName) || getExtension(previewTemplate.s3_path)
    : "";
  const previewUrl = previewTemplate
    ? getPreviewUrl(previewTemplate.s3_path, previewExt)
    : null;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <p>Loading templates...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        margin: "auto",
        width: "100%",
        maxWidth: "1200px",
        padding: "1rem",
      }}
    >
      <h4 style={{ textAlign: "center" }}>Discharge Templates</h4>

      {templates.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px", color: "#999" }}>
          <p>No templates uploaded yet</p>
        </div>
      ) : (
        <Grid container spacing={2}>
          {templates.map((template) => {
            const fileName = getDisplayName(template);
            const ext = getExtension(fileName) || getExtension(template.s3_path);

            return (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Box
                  onClick={() => {
                    if (!template.s3_path) {
                      toast.error("File URL is not available");
                      return;
                    }
                    setPreviewTemplate(template);
                  }}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "20px 16px",
                    textAlign: "center",
                    backgroundColor: "#fafafa",
                    cursor: template.s3_path ? "pointer" : "default",
                    "&:hover": template.s3_path
                      ? {
                          backgroundColor: "#f0f4f8",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          borderColor: "#0E3F66",
                        }
                      : {},
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>📄</div>
                  <h6
                    style={{
                      margin: "0 0 6px 0",
                      color: "#213360",
                      wordBreak: "break-word",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {fileName}
                  </h6>
                  {ext && (
                    <p
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "11px",
                        color: "#888",
                        textTransform: "uppercase",
                      }}
                    >
                      {ext}
                    </p>
                  )}
                  {template.s3_path && (
                    <p style={{ margin: 0, fontSize: "11px", color: "#0E3F66" }}>
                      Click to view
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(template.id, fileName);
                    }}
                    style={{
                      marginTop: "8px",
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      fontSize: "11px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {previewTemplate && (
        <Modal
          open={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <Box
            sx={{
              width: "90vw",
              height: "90vh",
              backgroundColor: "#fff",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                borderBottom: "1px solid #eee",
                gap: "12px",
              }}
            >
              <strong
                style={{
                  color: "#213360",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {previewName}
              </strong>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                {previewTemplate?.s3_path && (
                  <button
                    type="button"
                    className="btn btn__secondary"
                    onClick={() =>
                      window.open(
                        previewTemplate.s3_path,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    style={{
                      backgroundColor: "#0E3F66",
                      color: "#fff",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "13px",
                    }}
                  >
                    Open original
                  </button>
                )}
                <button
                  type="button"
                  className="btn"
                  onClick={() => setPreviewTemplate(null)}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "13px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            {previewUrl ? (
              <iframe
                key={previewTemplate?.id}
                title={previewName}
                src={previewUrl}
                style={{ flex: 1, width: "100%", border: "none" }}
              />
            ) : (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <p style={{ fontSize: "16px", color: "#666", marginBottom: "20px" }}>
                  Preview not available for this file type
                </p>
                <button
                  type="button"
                  className="btn btn__secondary"
                  onClick={() =>
                    window.open(
                      previewTemplate.s3_path,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  style={{
                    backgroundColor: "#0E3F66",
                    color: "#fff",
                    borderRadius: "6px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    cursor: "pointer",
                    border: "none",
                  }}
                >
                  Download File
                </button>
              </div>
            )}
          </Box>
        </Modal>
      )}
    </div>
  );
}

export default React.memo(DischargeTemplateList);
